import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from "react";
import { detectLanguageFromPath } from "../helper/language";
import i18n from "i18next";
import { I18nextProvider, initReactI18next, useTranslation } from "react-i18next";
import deQuestions from "../locales/de/question-texts.json";
import deUi from "../locales/de/ui-texts.json";
import deMedication from "../locales/de/medication-recommendation.json";
import enQuestions from "../locales/en/question-texts.json";
import enUi from "../locales/en/ui-texts.json";
import enMedication from "../locales/en/medication-recommendation.json";

const resources = {
  de: {
    translation: {
      ...deUi,
      questions: deQuestions,
      "medication-recommendation": deMedication,
    },
  },
  en: {
    translation: {
      ...enUi,
      questions: enQuestions,
      "medication-recommendation": enMedication,
    },
  },
};

if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources,
    lng: "de",
    fallbackLng: "de",
    interpolation: {
      escapeValue: false,
    },
  });
}

const LanguageContext = createContext({ language: "de", setLanguage: () => {} });

export const useLanguage = () => useContext(LanguageContext);

export const useUiTranslation = () => {
  const { t } = useTranslation();
  return useMemo(
    () => (key, options) => t(key, options),
    [t]
  );
};

export const useQuestionTranslation = () => {
  const { t } = useTranslation();
  return useMemo(
    () => (key, options) => t(`questions.${key}`, options),
    [t]
  );
};

const LanguageProvider = ({ initialLanguage = "de", children }) => {
  const resolvedInitialLanguage = useMemo(() => {
    if (typeof window !== "undefined") {
      return detectLanguageFromPath(window.location.pathname);
    }
    return initialLanguage;
  }, [initialLanguage]);

  const [language, setLanguageState] = useState(resolvedInitialLanguage);

  useEffect(() => {
    setLanguageState(resolvedInitialLanguage);
  }, [resolvedInitialLanguage]);

  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language]);

  const setLanguage = useCallback((nextLanguage) => {
    setLanguageState(nextLanguage);
  }, []);

  const value = useMemo(() => ({ language, setLanguage }), [language, setLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
